import { useEffect, useLayoutEffect, useState } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import { App, Avatar, Divider, Dropdown, Flex, Layout, Space, Tag, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import InputComponent from "../../Components/Input"
import Private, { INCOGNITO_MASK } from "../../Components/Private"
import Icon from "../../Icon"
import Images from "../../Images"
import { selectIncognito } from "../../Utils/incognito"
import { isOnboardingFinished as getIsOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { adminRoleId } from "../../Utils/permission"
import utils, { createAccountStepPath, PARTNER_ADMIN_ID, statusTextMap } from "../../Utils/utils"
import AuthCreators from "../LoginPage/reducer"
import MenuComponent from "../User"
import UserCreators from "../User/reducer"
import NavGroup from "./components/NavGroup/NavGroup"
import {
  ADMIN_HIDDEN_URLS,
  filterNavIconsByHiddenUrls,
  navIconsAdmin,
  navIconsArr,
  NavLinkWithIcon,
  PARTNER_ADMIN_HIDDEN_URLS
} from "./navigation"
import { getRestrictedAdminMenuItems } from "./utils"

import "./styles.less"

const { Header } = Layout
const { Text, Paragraph } = Typography

const HeaderComponent = ({
  theme,
  defaultTheme,
  isMobile,
  logout,
  isAdmin,
  userInfo,
  businessAccountsFetching,
  businessAccountDeleting,
  businessAccountsWithRole,
  businessAccounts,
  businessAccount,
  nextCreateAccountStep,
  demoRef,
  saveFetching,
  saveResult,
  incognito,
  ...props
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification } = App.useApp()

  const [switchAccount, setSwitchAccount] = useState(false)
  const [switchAccountAndGoSettings, setSwitchAccountAndGoSettings] = useState(false)
  const [search, setSearch] = useState("")
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)

  const baCount = businessAccountsWithRole?.length
  const isActive = (path) => location?.pathname?.includes(path)
  const isOnboardingFinished = getIsOnboardingFinished(userInfo)
  let businessId = userInfo?.["current_business_account_id"]
  const activeAccount = businessAccountsWithRole?.filter((el) => el?.id === businessId)?.[0] || {
    name: null,
    id: null
  }
  const filteredBusinessAccounts = businessAccountsWithRole?.filter((a) =>
    a.name.toLowerCase().includes(search.toLowerCase())
  )
  const isPartnerAdmin = userInfo.role.id === PARTNER_ADMIN_ID
  const isCommonAdmin = userInfo.role.id === adminRoleId
  const isRestrictedInvoicesAdmin = userInfo?.role?.id === 5
  const filteredNavIconsAdmin = isPartnerAdmin
    ? filterNavIconsByHiddenUrls(navIconsAdmin, PARTNER_ADMIN_HIDDEN_URLS)
    : isCommonAdmin
      ? filterNavIconsByHiddenUrls(navIconsAdmin, ADMIN_HIDDEN_URLS)
      : navIconsAdmin

  const adminMenuIcons = isRestrictedInvoicesAdmin
    ? getRestrictedAdminMenuItems(filteredNavIconsAdmin)
    : filteredNavIconsAdmin

  const menuIcons = isAdmin ? adminMenuIcons : navIconsArr
  const isDemo =
    isAdmin && location?.pathname?.includes("/admin/")
      ? businessAccount?.["is_demo"]
      : userInfo?.["is_demo"]
  const isLightMode = userInfo?.is_light_mode

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const findIncomplete = businessAccounts?.find(
    (acc) => acc.status === utils.toLowerLatter(statusTextMap.incomplete)
  )
  const isRedirectedToCompleteBa = localStorage.getItem("isRedirectedToCompleteBa") === "true"

  if (findIncomplete && isOnboardingFinished && !isAdmin && !isRedirectedToCompleteBa) {
    const nextStep = +findIncomplete.completed_steps[0] + 1
    props.changeNextCreateAccountStep(nextStep)
    if (nextCreateAccountStep) {
      localStorage.setItem("isRedirectedToCompleteBa", "true")
      props.changeBusinessAccount(findIncomplete.id)
      navigate(createAccountStepPath[nextStep], {
        state: { nextCreateAccountStep: nextStep }
      })
    }
  }

  useLayoutEffect(() => {
    if (!!userInfo && !isAdmin) {
      props.getBusinessAccountsWithRole()
      props.getBusinessAccounts()
    }
  }, [])

  useEffect(() => {
    if (saveResult) {
      if (switchAccount) {
        setSwitchAccount(false)

        notification.success({
          message: t("Business account changed"),
          description: t("You’ve changed your business account successfully."),
          ...notificationConfig
        })

        props.getUser()

        if (switchAccountAndGoSettings) {
          setSwitchAccountAndGoSettings(false)
          navigate("/business_settings/my_business")
        }
        props.userSaveResultReset()
      }

      if (saveResult === "business_created" && baCount > 1) {
        notification.success({
          message: t("Business created"),
          description: t("Your business has been successfully created."),
          ...notificationConfig
        })
        props.userSaveResultReset()
      }
    }
  }, [saveResult, switchAccount])

  const handleAddNewAccount = () => {
    props.userSaveResultReset()
    props.changeNextCreateAccountStep(1)
    props.setRedirectAfterCreateAccount(location?.pathname)
    props.getBusinessAccounts()
    navigate(createAccountStepPath[1])
  }

  const handleVisibleChange = (value) => {
    setIsDropdownVisible(value)
    setSearch("")
  }

  const handleChangeSearch = (e) => {
    const value = e.target.value
    setSearch(value)
  }

  const handleChangeBusinessAccount = (newBusinessAccountId) => {
    if (newBusinessAccountId === businessId) return
    setSwitchAccount(true)
    props.changeBusinessAccount(newBusinessAccountId)
    setIsDropdownVisible(false)
  }

  const getAvatar = (businessAccount) => (
    <Avatar
      className="user_avatar"
      size={24}
      style={{
        backgroundColor: businessAccount?.photo ? "transparent" : "#225AEA",
        borderRadius: "8px",
        border: "none",
        fontSize: 14,
        marginRight: 8,
        minWidth: 24
      }}
      {...(businessAccount?.photo && { src: businessAccount?.photo })}
    >
      {!businessAccount?.photo ? businessAccount?.name?.[0] : null}
    </Avatar>
  )
  return (
    <Header className="header">
      <Link to={isAdmin ? "/alerts" : "/dashboard"} className="logo-link">
        <img
          src={
            isAdmin
              ? Images?.[theme === "dark" ? "logoWithTextAdminWhite" : "logoWithTextAdmin"]
              : Images?.[theme === "dark" ? "logoWithTextWhite" : "logoWithText"]
          }
          height={40}
          width={222}
          alt="logo"
        />
      </Link>

      {!isAdmin && <Divider type="vertical" />}

      {!isAdmin && (
        <Dropdown
          overlayClassName={"custom-menu header-menu"}
          getPopupContainer={() => document.getElementById("main-layout")}
          overlayStyle={{ width: 300 }}
          disabled={switchAccount}
          onOpenChange={handleVisibleChange}
          open={isDropdownVisible}
          menu={{
            items: [
              baCount > 5 && {
                key: "search",
                label: (
                  <div className="dropdown-search-wrapper">
                    <InputComponent
                      className="dropdown-search"
                      prefix={<Icon type="search" role="icon" />}
                      placeholder={`${t("Search")}...`}
                      value={search}
                      onChange={handleChangeSearch}
                    />
                  </div>
                )
              },
              [...filteredBusinessAccounts]?.map((ba) => ({
                key: ba.id,
                //title: ba.name,
                label: (
                  <Flex
                    align="center"
                    gap={4}
                    onClick={() => handleChangeBusinessAccount(ba.id)}
                    wrap="nowrap"
                  >
                    {getAvatar(ba)}
                    <Paragraph
                      ellipsis={{
                        rows: 1,
                        expandable: true,
                        tooltip: incognito ? INCOGNITO_MASK : ba.name,
                        symbol: null
                      }}
                      style={{ margin: 0 }}
                    >
                      <Private>{ba.name}</Private>
                    </Paragraph>
                    {ba.role ? (
                      <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {ba?.role?.name}
                      </Text>
                    ) : null}
                    <Flex align="center" justify="center" style={{ width: 24, marginLeft: "auto" }}>
                      {activeAccount.id === ba.id ? (
                        <Icon type="circle_check" style={{ fontSize: 16, margin: "0 auto" }} />
                      ) : (
                        <ButtonComponent
                          type="text"
                          size="small"
                          className="flex center edit-btn"
                          onClick={() => setSwitchAccountAndGoSettings(true)}
                          icon={
                            <Tooltip title={t("Edit")}>
                              <Icon type="edit_small" />
                            </Tooltip>
                          }
                        />
                      )}
                    </Flex>
                  </Flex>
                )
              })),
              {
                key: "add_account",
                //title: 'Add account',
                label: (
                  <Flex align="center" onClick={handleAddNewAccount}>
                    <Icon
                      type={theme === "dark" ? "add_account_dark" : "add_account"}
                      style={{ fontSize: 24, marginRight: 12 }}
                    />
                    <Text type="secondary">{t("Create new business")}</Text>
                  </Flex>
                )
                //label: <Link to={`/business_settings/my_business`}><Text type="secondary">{t('Add account')}</Text></Link>,
              }
            ]
            //selectable: true,
            //selectedKeys: [activeAccount.id],
          }}
          trigger={["click"]}
          placement="bottomLeft"
        >
          <div className="custom-trigger-block" onClick={(e) => e.stopPropagation()}>
            {getAvatar(activeAccount)}
            <Text
              ellipsis={{
                tooltip: incognito ? INCOGNITO_MASK : activeAccount.name
              }}
              strong
            >
              <Private>{activeAccount.name}</Private>
            </Text>
            {businessAccountsFetching || switchAccount ? (
              <LoadingOutlined />
            ) : (
              <Icon type="chevron_down_small" />
            )}
          </div>
        </Dropdown>
      )}

      {isDemo && !isAdmin && !isLightMode ? (
        <>
          <Divider type="vertical" />
          <Tag
            ref={demoRef}
            bordered={false}
            color="error"
            style={{ fontSize: 14, padding: "3px 8px" }}
            className={"color-dark"}
          >
            <Flex gap={4} align={"center"}>
              <Icon type={"user_circle"} style={{ fontSize: 20 }} />
              {t("Demo-mode")}
            </Flex>
          </Tag>
        </>
      ) : null}

      <div className="navigate-wrap">
        {menuIcons.map(
          ({ url, icon, text, comingSoon, markText, subUrl, isGroup, items, groupTitle }) =>
            isGroup ? (
              <NavGroup key={groupTitle} icon={icon} groupTitle={groupTitle} items={items} />
            ) : (
              <NavLinkWithIcon
                key={url}
                url={url}
                theme={theme}
                text={text}
                active={isActive(subUrl)}
                icon={icon}
                isSoon={comingSoon}
                markText={markText || null}
              />
            )
        )}
      </div>

      <Space size={16} style={{ height: 40 }} align="middle">
        <MenuComponent />
      </Space>
    </Header>
  )
}

const mapStateToProps = (state) => ({
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,
  defaultTheme: state.nav.defaultTheme,
  theme: state.nav.theme,
  incognito: selectIncognito(state),

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,
  nextCreateAccountStep: state.user.nextCreateAccountStep,

  businessAccountsFetching: state.user.businessAccountsWithRoleFetching,
  businessAccountsWithRole: state.user.businessAccountsWithRole,
  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount
})

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(AuthCreators.logout()),
  getBusinessAccounts: () => dispatch(UserCreators.getBusinessAccountsRequest()),
  getBusinessAccountsWithRole: () => dispatch(UserCreators.getBusinessAccountsWithRoleRequest()),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(
      UserCreators.userSaveRequest({
        current_business_account_id: newBusinessAccountId
      })
    ),
  userSaveResultReset: () => dispatch(UserCreators.userSaveResultReset()),
  getUser: () => dispatch(UserCreators.userRequest()),
  changeNextCreateAccountStep: (nextCreateAccountStep) =>
    dispatch(UserCreators.changeNextCreateAccountStep(nextCreateAccountStep)),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(UserCreators.userSetProp("redirectAfterCreateAccount", location))
})

export default connect(mapStateToProps, mapDispatchToProps)(HeaderComponent)
